import React from "react";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";

import styles from "./VivaPayslip.module.css";

/* ---------- helpers ---------- */
const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const fmtDate = (value, pattern) => {
  const d = toDate(value);
  return d ? format(d, pattern) : "";
};

const isEmpty = (value) =>
  value === null || value === undefined || Number(value) === 0;

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// "N.A" style (Overtime, Commission, Income Tax ...)
const moneyOrNA = (value) => (isEmpty(value) ? "N.A" : money(value));
// "NA" style (CPF, Agency) – matches the PDF
const moneyOrNa = (value) => (isEmpty(value) ? "NA" : money(value));

// Sum amounts from a list whose description matches any of the patterns
const pickAmount = (list = [], patterns = []) =>
  list
    .filter((item) =>
      patterns.some((p) => p.test(String(item?.description || ""))),
    )
    .reduce((sum, item) => sum + Number(item?.amount || 0), 0);

/* ---------- component ---------- */
const Viva_Payslip = ({ payrollDetails = {}, companyDetails = {} }) => {
  const earnings = payrollDetails?.earningsList || [];
  const deductions = payrollDetails?.deductionsList || [];
  const additions = payrollDetails?.additionsList || [];

  const company = {
    ...companyDetails,
    addressLines: [
      companyDetails?.address1,
      companyDetails?.address2,
      companyDetails?.address3,
      `${companyDetails?.country} - ${companyDetails?.zipCode}`,
    ],
  };

  /* earnings */
  const salary =
    payrollDetails?.basicSalary ??
    pickAmount(earnings, [/basic/i, /^salary$/i]);
  const overtime =
    payrollDetails?.otTotal ?? pickAmount(earnings, [/^ot/i, /over\s*time/i]);
  const commission =
    payrollDetails?.commission ?? pickAmount(earnings, [/commi/i]);
  const allowances =
    payrollDetails?.allowanceTotal ??
    pickAmount(earnings, [/allowance/i, /food/i]);
  const accommodation =
    payrollDetails?.accommodation ?? pickAmount(earnings, [/accom/i]);
  const grossPay = payrollDetails?.grossSalary;

  /* deductions */
  const cpfEmployer = payrollDetails?.employerCpf;
  const cpfEmployee =
    payrollDetails?.employeeCpf ?? pickAmount(deductions, [/cpf/i]);
  const agencyFee =
    payrollDetails?.agencyFee ?? pickAmount(deductions, [/agency/i]);
  const advanceLoan =
    payrollDetails?.advanceLoan ??
    pickAmount(deductions, [/advance/i, /loan/i]);
  const incomeTax =
    payrollDetails?.incomeTax ?? pickAmount(deductions, [/tax/i]);
  const totalDeductions = payrollDetails?.totalDeduction;

  /* additions */
  const reimbursement =
    payrollDetails?.reimbursement ?? pickAmount(additions, [/reimburse/i]);
  const totalAdditions =
    payrollDetails?.totalAddition ??
    additions.reduce((sum, a) => sum + Number(a?.amount || 0), 0);

  const netPay = payrollDetails?.netPay;

  const period = `${fmtDate(payrollDetails?.fromDate, "do MMMM yyyy")} To ${fmtDate(
    payrollDetails?.toDate,
    "do MMMM yyyy",
  )}`;
  const payDate = fmtDate(payrollDetails?.paymentDate, "d/M/yyyy");
  const finNo = payrollDetails?.finNumber || payrollDetails?.icno || "";

  const ALWAYS_SHOW = ["Incentive Pay", "Other Allowance"];

  const visibleEarnings = (payrollDetails?.earningsList || []).filter(
    (pd) => Number(pd?.amount) !== 0 || ALWAYS_SHOW.includes(pd?.description),
  );
  const earningRowCount = Math.max(visibleEarnings.length, 3);
  const authorisedRowSpan = earningRowCount - 2;

  return (
    <div className={styles.page}>
      {/* ---------- header ---------- */}
      <header className={styles.header}>
        <div className={styles.companyInfo}>
          <p>{company.companyName}</p>
          {company.regNo && <p>Company Reg: {company.regNo}</p>}
          {company.addressLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
          {(company.phoneNo || company.faxNo) && (
            <p>
              {company.phoneNo && `Tel: ${company.phoneNo}`}
              {company.phoneNo && company.faxNo && " | "}
              {company.faxNo && `Fax: ${company.faxNo}`}
            </p>
          )}
          {company.email && <p>E-Mail: {company.email}</p>}
        </div>

        {companyDetails?.companyLogo && (
          <img
            className={styles.logo}
            src={`data:image/png;base64,${companyDetails.companyLogo}`}
            alt="Company Logo"
            width={250}
          />
        )}
      </header>

      {/* ---------- main grid ---------- */}
      <table className={styles.grid}>
        <colgroup>
          <col className={styles.colLabel} />
          <col className={styles.colValue} />
          <col className={styles.colMidLabel} />
          <col className={styles.colRight} />
        </colgroup>
        <tbody>
          <tr>
            <td className={styles.label}>Employee Name:</td>
            <td className={styles.center}>{payrollDetails?.fullName}</td>
            <td className={styles.label}>Month:</td>
            <td className={styles.periodValue}>{period}</td>
          </tr>
          <tr>
            <td className={styles.label}>Nric/Fin No:</td>
            <td className={styles.center}>{finNo}</td>
            <td className={styles.label}>Pay Date:</td>
            <td className={styles.periodValue}>{payDate}</td>
          </tr>

          {/* earnings */}
          {Array.from({ length: earningRowCount }).map((_, i) => {
            const pd = visibleEarnings[i];

            return (
              <tr className={styles.sectionStart} key={i}>
                <td className={styles.label}>{pd?.description}</td>
                <td colSpan={2} className={styles.center}>
                  {money(pd?.amount || 0)}
                </td>

                {/* right side */}
                {i === 0 && (
                  <>
                    <td
                      className={`${styles.center} ${styles.blockTitleSmall}`}
                    >
                      Payment Mode: {payrollDetails?.paymentMode}
                    </td>
                  </>
                )}
                {i === 1 && (
                  <>
                    <td rowSpan={5} className={styles.blockCell}>
                      <div className={styles.blockTitle}>Prepared By:</div>
                      <div>{payrollDetails?.reviewedBy}</div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
          {/* <tr className={styles.sectionStart}>
            <td className={styles.label}>Salary</td>
            <td colSpan={2} className={styles.center}>
              {money(salary)}
            </td>
            <td className={`${styles.center} ${styles.blockTitleSmall}`}>
              Payment Mode:
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Overtime</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(overtime)}
            </td>
            <td rowSpan={2} className={styles.blockCell}>
              {payrollDetails?.paymentMode}
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Commission</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(commission)}
            </td>
          </tr>
          <tr className={styles.sectionStart}>
            <td className={styles.label}>Allowances/Food</td>
            <td colSpan={2} className={styles.center}>
              {money(allowances)}
            </td>
            <td rowSpan={5} className={styles.blockCell}>
              <div className={styles.blockTitle}>Prepared By:</div>
              <div>{payrollDetails?.reviewedBy}</div>
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Accomodation</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(accommodation)}
            </td>
          </tr>*/}
          <tr className={styles.totalRow}>
            <td className={styles.label}>(A) GROSS PAY</td>
            <td colSpan={2} className={`${styles.center} ${styles.bold}`}>
              {money(grossPay)}
            </td>
            <td><div className={styles.blockTitle}><center>Authorised By:</center></div></td>
          </tr>

          {/* deductions */}
          {payrollDetails?.deductionsList.map(
            (pd, _k) => (
              // Number(pd?.amount) !== Number(0) && (
              <tr key={_k}>
                <td className={styles.label}>{pd?.description}</td>
                <td colSpan={2} className={styles.center}>{moneyOrNA(pd?.amount)}</td>

                <td
                  rowSpan={payrollDetails?.deductionsList.length + 3}
                  className={styles.blockCell}
                >
                  {/* <div className={styles.blockTitle}>Authorised By:</div> */}
                  {/* <div>{payrollDetails?.reviewedBy}</div> */}
                  <div></div>
                </td>
              </tr>
            ),

            // <div className={styles.payrollListRow} key={_k}>
            //   <span className={styles.label}>{pd?.description}</span>
            //   <span className={styles.value}>
            //     {Number(pd?.amount).toFixed(2)}
            //   </span>
            // </div>
            // )
          )}
          {/* <tr>
            <td className={styles.label}>CPF- Employer</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNa(cpfEmployer)}
            </td>
          </tr>
          <tr>
            <td className={styles.label}>CPF -Employee</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNa(cpfEmployee)}
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Agency(NA)</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNa(agencyFee)}
            </td>
            <td className={`${styles.center} ${styles.blockTitle}`}>
              Authorised By:
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Advance / Loan</td>
            <td colSpan={2} className={styles.center}>
              {money(advanceLoan)}
            </td>
            <td rowSpan={6} className={styles.stampCell}>
              {companyDetails?.companyStamp ? (
                <img
                  className={styles.stamp}
                  src={`data:image/png;base64,${companyDetails.companyStamp}`}
                  alt="Company Stamp"
                />
              ) : (
                payrollDetails?.approvedBy
              )}
            </td>
          </tr>
          <tr>
            <td className={styles.label}>Income Tax</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(incomeTax)}
            </td>
          </tr> */}
          <tr className={styles.totalRow}>
            <td className={styles.label}>(B) TOTAL DEDUCTIONS</td>
            <td colSpan={2} className={styles.center}>
              {money(totalDeductions)}
            </td>
          </tr>

          {/* additions */}
          <tr>
            <td className={styles.label}>Reimbursement</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(reimbursement)}
            </td>
          </tr>
          <tr className={styles.totalRow}>
            <td className={styles.label}>(C ) TOTAL ADDITIONS</td>
            <td colSpan={2} className={styles.center}>
              {moneyOrNA(totalAdditions)}
            </td>
          </tr>
          <tr className={styles.totalRow}>
            <td className={styles.label}>Net Pay (A+B+C)</td>
            <td colSpan={2} className={`${styles.center} ${styles.bold}`}>
              {money(netPay)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ---------- footer ---------- */}
      <p className={styles.naNote}>*N.A.-Not Applicable</p>
      <p className={styles.footerNote}>
        THIS IS A COMPUTER GENERATED DOCUMENT THAT REQUIRES NO SIGNATURE
      </p>
      <p className={styles.copyright}>&copy;{company.companyName}</p>
    </div>
  );
};

Viva_Payslip.propTypes = {
  payrollDetails: PropTypes.object.isRequired,
  companyDetails: PropTypes.object.isRequired,
};

export default Viva_Payslip;
